"use client";

import Image from "next/image";
import { useCallback, useEffect, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  type CarouselApi,
} from "@/components/ui/carousel";

interface ProductGalleryProps {
  images: string[];
  productName: string;
}

export default function ProductGallery({ images, productName }: ProductGalleryProps) {
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    if (!api) return;

    setCurrent(api.selectedScrollSnap());

    api.on("select", () => {
      setCurrent(api.selectedScrollSnap());
    });
  }, [api]);

  const scrollTo = useCallback(
    (index: number) => {
      api?.scrollTo(index);
    },
    [api]
  );

  const scrollPrev = useCallback(() => {
    api?.scrollPrev();
  }, [api]);

  const scrollNext = useCallback(() => {
    api?.scrollNext();
  }, [api]);

  return (
    <div className="flex flex-col-reverse sm:flex-row gap-3 md:gap-4">
      {/* Thumbnails - Horizontal on mobile, Vertical on desktop */}
      <div className="flex sm:flex-col gap-2 md:gap-3 overflow-x-auto sm:overflow-x-visible pb-2 sm:pb-0">
        {images.map((img, i) => (
          <button
            key={i}
            onClick={() => scrollTo(i)}
            className={`relative w-14 h-14 sm:w-16 sm:h-16 md:w-20 md:h-20 rounded-lg sm:rounded-xl overflow-hidden border-2 bg-background-50 cursor-pointer transition-colors flex-shrink-0 ${
              current === i ? "border-stroke-dark" : "border-transparent hover:border-stroke"
            }`}
          >
            <Image src={img} alt="" fill className="object-contain p-1.5 sm:p-2" />
          </button>
        ))}
      </div>

      {/* Main Image Carousel */}
      <div className="relative flex-1">
        <Carousel setApi={setApi} opts={{ loop: true }} className="w-full">
          <CarouselContent>
            {images.map((img, i) => (
              <CarouselItem key={i}>
                <div className="aspect-square bg-background-50 rounded-xl sm:rounded-2xl overflow-hidden relative">
                  <Image
                    src={img}
                    alt={`${productName} - Image ${i + 1}`}
                    fill
                    className="object-contain p-4 sm:p-6 md:p-8"
                  />
                </div>
              </CarouselItem>
            ))}
          </CarouselContent>

          {/* Navigation Arrows */}
          <button
            onClick={scrollPrev}
            className="absolute left-1 sm:left-2 md:left-4 top-1/2 -translate-y-1/2 cursor-pointer z-10"
          >
            <ChevronLeft className="h-5 w-5 sm:h-6 sm:w-6 md:h-8 md:w-8 text-[#7D8082]" />
          </button>
          <button
            onClick={scrollNext}
            className="absolute right-1 sm:right-2 md:right-4 top-1/2 -translate-y-1/2 cursor-pointer z-10"
          >
            <ChevronRight className="h-5 w-5 sm:h-6 sm:w-6 md:h-8 md:w-8 text-[#7D8082]" />
          </button>
        </Carousel>
      </div>
    </div>
  );
}
